// Lib
import { clamp } from 'lodash';

// Types
import { Matrix3D } from 'rematrix';
import { Point } from '../../../common/maths/geometry/pointTypes';
import { Rect, RectSize } from '../../../common/maths/geometry/rect/rectTypes';

// Utils
import { getMinScale } from './zoomScaleUtils';
import { getMinScalePadding } from './zoomScrollAreaSizeUtils';
import * as rectLib from '../../../common/maths/geometry/rect';
import * as pointLib from '../../../common/maths/geometry/point';
import * as matrixLib from '../../../common/maths/matrix/matrixUtils';
import * as rectMatrixLib from '../../../common/maths/matrix/rectMatrixUtils';

// Constants
import { ZoomPointMode } from './zoomPointModeUtils';

/**
 * Translates an event's client position into the coordinates of the canvas
 * and ensures they sit inside the bounds of the transformed canvas.
 */
const zoomFromEventPosition = (
    eventClientPosition: Point,
    canvasViewportClientRect: DOMRect,
    canvasScroll: Point,
    canvasDocumentSize: RectSize,
    transformationMatrix: Matrix3D,
): Point => {
    const hasAllMeasurements =
        !!eventClientPosition &&
        !!canvasViewportClientRect &&
        !!canvasScroll &&
        !!canvasDocumentSize &&
        !!transformationMatrix;

    if (!hasAllMeasurements) return eventClientPosition || { x: 0, y: 0 };

    const originalCanvasSizePoints = rectMatrixLib.convertToRectPoints({
        ...canvasDocumentSize,
        x: 0,
        y: 0,
        left: 0,
        top: 0,
        right: canvasDocumentSize.width,
        bottom: canvasDocumentSize.height,
    });

    const transformedCanvasSize = matrixLib.transformAllPoints(transformationMatrix, originalCanvasSizePoints);

    const xPoints = transformedCanvasSize.map((point) => point[0]);
    const yPoints = transformedCanvasSize.map((point) => point[1]);

    const minX = Math.min(...xPoints);
    const maxX = Math.max(...xPoints);
    const minY = Math.min(...yPoints);
    const maxY = Math.max(...yPoints);

    // Gets the scale position in the coordinates of the Canvas
    const eventClientPositionCanvasCoords = pointLib.difference(
        {
            x: canvasViewportClientRect.left - canvasScroll.x,
            y: canvasViewportClientRect.top - canvasScroll.y,
        },
        // @ts-ignore: FIXME-TS - struggles with currying
        eventClientPosition,
    );

    // Ensure that the scroll position doesn't sit outside the canvas boundary
    return {
        x: clamp(eventClientPositionCanvasCoords.x, minX, maxX),
        y: clamp(eventClientPositionCanvasCoords.y, minY, maxY),
    };
};

/**
 * Determines the value along a single dimension to use, to ensure that the canvas will be centred in
 * the viewport when we reach the minimum scale.
 *
 * FIXME-ZOOM - Create a diagram explaining this maths.
 * FIXME-ZOOM - This needs to account for scroll on boards that are too large to fit within the viewport
 *  at the minimum zoom.
 */
const calculateDimensionForCentreZoom = (
    initialScale: number,
    minScale: number,
    initialPadding: number,
    minScalePadding: number,
    scroll: number,
): number => ((minScale / initialScale) * initialPadding - scroll - minScalePadding) / (minScale / initialScale - 1);

/**
 * The initial translation of the canvas elements, is their translation from the top left of the canvas document,
 * plus the translation of the canvas document.
 */
const getInitialTranslation = (transformationMatrix: Matrix3D, canvasElementsOrigin: Point) => {
    // Scale the canvas elements into their rendered position
    const initialScale = matrixLib.getScale(transformationMatrix);
    const canvasElementsOriginScaled = pointLib.scale(initialScale, canvasElementsOrigin);
    const translation = matrixLib.getTranslation(transformationMatrix);
    return pointLib.translate(translation, canvasElementsOriginScaled);
};

/**
 * Determines the point to use in order to zoom out to centre the canvas document within the canvas viewport
 * at the minimum scale.
 *
 * The point to use will be the point that is constant throughout the transformation.
 * I.e. There will be one point that doesn't change position during each scale of the zoom out. We need to
 * find that position and then use it during the zoom operation.
 */
const zoomFromCentreOfCanvas = (
    canvasViewportClientRect: DOMRect,
    canvasScroll: Point,
    canvasDocumentSize: RectSize,
    contentRect: Rect,
    transformationMatrix: Matrix3D,
    targetScale = getMinScale(canvasViewportClientRect, contentRect),
): Point => {
    let initialScale = matrixLib.getScale(transformationMatrix);

    // FIXME This logic might need to be changed when we have two stage zoom out (fit then centred)
    const isInitiallyTargetScale = initialScale === targetScale;

    // If the initial scale is the min scale, then just treat it like we're going back to full scale
    initialScale = isInitiallyTargetScale ? 1 : initialScale;
    const initialTranslation = isInitiallyTargetScale
        ? { x: 0, y: 0 }
        : getInitialTranslation(transformationMatrix, contentRect);

    // FIXME if the target scale is > 1 then this is probably not correct!
    const padding = getMinScalePadding(canvasViewportClientRect, contentRect, targetScale);

    return {
        x: calculateDimensionForCentreZoom(initialScale, targetScale, initialTranslation.x, padding.x, canvasScroll.x),
        y: calculateDimensionForCentreZoom(initialScale, targetScale, initialTranslation.y, padding.y, canvasScroll.y),
    };
};

/**
 * Determines the padding that's currently visible in the viewport.
 */
const getVisiblePadding = (
    canvasViewportClientRect: DOMRect,
    canvasScroll: Point,
    canvasDocumentSize: RectSize,
    transformationMatrix: Matrix3D,
): Rect => {
    const currentScale = matrixLib.getScale(transformationMatrix);
    const currentPadding = matrixLib.getTranslation(transformationMatrix);

    // Find out how much padding is showing
    return rectLib.asRect({
        left: currentPadding.x - canvasScroll.x,
        top: currentPadding.y - canvasScroll.y,
        right:
            canvasViewportClientRect.width +
            canvasScroll.x -
            currentPadding.x -
            canvasDocumentSize.width * currentScale,
        bottom:
            canvasViewportClientRect.height +
            canvasScroll.y -
            currentPadding.y -
            canvasDocumentSize.height * currentScale,
    });
};

/**
 * Determines the boundaries for the transform position when in a scale that's less than 100%.
 * When zooming back in, we want to make sure that no empty space shows when returning to 100%. So we need
 * to make sure that the point that we use is within a boundary so that the empty space is completely cleared
 * when we return to 100%.
 */
const getTransformBoundaryZoomedOut = (
    canvasViewportClientRect: DOMRect,
    canvasScroll: Point,
    canvasDocumentSize: RectSize,
    transformationMatrix: Matrix3D,
): Rect => {
    const currentScale = matrixLib.getScale(transformationMatrix);
    const currentPadding = matrixLib.getTranslation(transformationMatrix);

    // Find out how much padding is showing
    const visiblePadding = getVisiblePadding(
        canvasViewportClientRect,
        canvasScroll,
        canvasDocumentSize,
        transformationMatrix,
    );

    // The padding needs to be 0 when the scaling finished. We use ceil & floor to ensure that rounding errors
    // don't result in slight gaps showing at the end
    return rectLib.asRect({
        left: Math.ceil(visiblePadding.left / (1 - currentScale) + canvasScroll.x),
        top: Math.ceil(visiblePadding.top / (1 - currentScale) + canvasScroll.y),
        right: Math.floor(
            currentPadding.x +
                canvasDocumentSize.width * currentScale -
                (visiblePadding.right * currentScale) / (1 - currentScale),
        ),
        bottom: Math.floor(
            currentPadding.y +
                canvasDocumentSize.height * currentScale -
                (visiblePadding.bottom * currentScale) / (1 - currentScale),
        ),
    });
};

/**
 * Zooms from the event position, but clamps it to the boundaries that are required when in a
 * zoomed out state (scale is less than 100%).
 */
const zoomFromLimitedEventPositionZoomedOut = (
    eventClientPosition: Point,
    canvasViewportClientRect: DOMRect,
    canvasScroll: Point,
    canvasDocumentSize: RectSize,
    transformationMatrix: Matrix3D,
): Point => {
    const eventPosTransformOrigin = zoomFromEventPosition(
        eventClientPosition,
        canvasViewportClientRect,
        canvasScroll,
        canvasDocumentSize,
        transformationMatrix,
    );

    const transformBoundary = getTransformBoundaryZoomedOut(
        canvasViewportClientRect,
        canvasScroll,
        canvasDocumentSize,
        transformationMatrix,
    );

    return {
        x: clamp(eventPosTransformOrigin.x, transformBoundary.left, transformBoundary.right),
        y: clamp(eventPosTransformOrigin.y, transformBoundary.top, transformBoundary.bottom),
    };
};

/**
 * Determines the limits to use when in a zoomed in state and we're returning to 100%.
 * This is conceptually the opposite to the zoomed out state, because here we need to make sure that
 * we're not zooming out from a point that will show empty space around the canvas at 100%.
 *
 * This means that our "min limit" will actually be determined by considering the bottom-right hand
 * corner of the canvas document and viewport to determine what point the transform position must
 * be greater than to ensure that no empty space is shown in the bottom right hand corner.
 *
 * Conversely, the "max limit" is found by considering the top-left corner and finding the point that
 * the transformation position must be less than to prevent showing empty space to the top and left
 * of the canvas at 100%.
 */
const getTransformBoundaryZoomedIn = (
    canvasViewportClientRect: DOMRect,
    canvasScroll: Point,
    canvasDocumentSize: RectSize,
    transformationMatrix: Matrix3D,
): Rect => {
    const currentScale = matrixLib.getScale(transformationMatrix);
    const currentPadding = matrixLib.getTranslation(transformationMatrix);

    // The max limit here is the point that will make the origin (top left point of the document)
    // scale back to the top left of the viewport
    const maxLimit = {
        x: canvasScroll.x + (currentPadding.x - canvasScroll.x) / (1 - currentScale),
        y: canvasScroll.y + (currentPadding.y - canvasScroll.y) / (1 - currentScale),
    };

    // The min limit here is the point that will make the extremes (at the bottom right of the document)
    // scale back to the bottom right of the viewport
    const minLimit = {
        x:
            (currentPadding.x +
                canvasDocumentSize.width * currentScale -
                canvasScroll.x -
                canvasViewportClientRect.width) /
                (1 - currentScale) +
            canvasViewportClientRect.width +
            canvasScroll.x,
        y:
            (currentPadding.y +
                canvasDocumentSize.height * currentScale -
                canvasScroll.y -
                canvasViewportClientRect.height) /
                (1 - currentScale) +
            canvasViewportClientRect.height +
            canvasScroll.y,
    };

    return rectLib.asRect({
        left: minLimit.x,
        top: minLimit.y,
        right: maxLimit.x,
        bottom: maxLimit.y,
    });
};

/**
 * Zooms from the event position, but clamps it to the boundaries that are required when in a
 * zoomed in state (scale is greater than 100%).
 */
const zoomFromLimitedEventPositionZoomedIn = (
    eventClientPosition: Point,
    canvasViewportClientRect: DOMRect,
    canvasScroll: Point,
    canvasDocumentSize: RectSize,
    transformationMatrix: Matrix3D,
): Point => {
    const eventPosTransformOrigin = zoomFromEventPosition(
        eventClientPosition,
        canvasViewportClientRect,
        canvasScroll,
        canvasDocumentSize,
        transformationMatrix,
    );

    const transformBoundary = getTransformBoundaryZoomedIn(
        canvasViewportClientRect,
        canvasScroll,
        canvasDocumentSize,
        transformationMatrix,
    );

    return {
        x: clamp(eventPosTransformOrigin.x, transformBoundary.left, transformBoundary.right),
        y: clamp(eventPosTransformOrigin.y, transformBoundary.top, transformBoundary.bottom),
    };
};

/**
 * Uses the cached transformation point, without transforming its coordinates into the canvas document coordinate
 * system (because the cached point should already be in the canvas document coordinate system), and then ensures
 * that it's within the zoomed out transform boundaries.
 * This is useful so that changes to the viewport or content orientation won't mean that the content will fly
 * off the screen when using a cached point.
 */
const zoomFromCachedLimitedPoint = (
    cachedTransformationPoint: Point,
    canvasViewportClientRect: DOMRect,
    canvasScroll: Point,
    canvasDocumentSize: RectSize,
    transformationMatrix: Matrix3D,
): Point => {
    const transformBoundary = getTransformBoundaryZoomedOut(
        canvasViewportClientRect,
        canvasScroll,
        canvasDocumentSize,
        transformationMatrix,
    );

    return {
        x: clamp(cachedTransformationPoint.x, transformBoundary.left, transformBoundary.right),
        y: clamp(cachedTransformationPoint.y, transformBoundary.top, transformBoundary.bottom),
    };
};

/**
 * Determines the transformation point to use based on the current zoom mode.
 */
export const getTransformationOrigin = (
    zoomPointMode: ZoomPointMode,
    cachedTransformationPoint: Point,
    eventClientPosition: Point,
    canvasViewportClientRect: DOMRect,
    canvasScroll: Point,
    canvasDocumentSize: RectSize,
    contentRect: Rect,
    transformationMatrix: Matrix3D,
    targetScale?: number,
): Point => {
    switch (zoomPointMode) {
        case ZoomPointMode.CACHED:
            return cachedTransformationPoint;
        case ZoomPointMode.CACHED_LIMITED:
            return zoomFromCachedLimitedPoint(
                cachedTransformationPoint,
                canvasViewportClientRect,
                canvasScroll,
                canvasDocumentSize,
                transformationMatrix,
            );
        case ZoomPointMode.EVENT_POS:
            return zoomFromEventPosition(
                eventClientPosition,
                canvasViewportClientRect,
                canvasScroll,
                canvasDocumentSize,
                transformationMatrix,
            );
        case ZoomPointMode.EVENT_POS_LIMITED_ZOOMED_IN:
            return zoomFromLimitedEventPositionZoomedIn(
                eventClientPosition,
                canvasViewportClientRect,
                canvasScroll,
                canvasDocumentSize,
                transformationMatrix,
            );
        case ZoomPointMode.EVENT_POS_LIMITED_ZOOMED_OUT:
            return zoomFromLimitedEventPositionZoomedOut(
                eventClientPosition,
                canvasViewportClientRect,
                canvasScroll,
                canvasDocumentSize,
                transformationMatrix,
            );
        default:
        case ZoomPointMode.CENTRED:
            return zoomFromCentreOfCanvas(
                canvasViewportClientRect,
                canvasScroll,
                canvasDocumentSize,
                contentRect,
                transformationMatrix,
                targetScale,
            );
    }
};
